import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="mb-4">
      <button
        onClick={onClick}
        className={`w-full text-left p-5 rounded-2xl border-2 transition-all duration-300 flex justify-between items-center bg-white dark:bg-slate-800 ${
          isOpen 
            ? 'border-pink-400 shadow-[4px_4px_0_0_rgba(244,63,94,0.15)] dark:border-pink-600 dark:shadow-[4px_4px_0_0_rgba(219,39,119,0.3)]' 
            : 'border-gray-200 dark:border-slate-700 hover:border-pink-200 dark:hover:border-slate-600 hover:shadow-sm'
        }`}
      >
        <span className={`font-bold text-lg ${isOpen ? 'text-pink-600 dark:text-pink-400' : 'text-gray-800 dark:text-gray-200'}`}>
          {question}
        </span>
        <motion.div 
          animate={{ rotate: isOpen ? 180 : 0 }}
          className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${isOpen ? 'bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400' : 'bg-gray-100 text-gray-500 dark:bg-slate-700 dark:text-gray-400'}`}
        >
          <SafeIcon icon={FiIcons.FiChevronDown} />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="p-5 pt-3 pb-6 text-gray-600 dark:text-gray-300 font-medium leading-relaxed ml-2 border-l-4 border-pink-100 dark:border-pink-900/30 mt-2">
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Help = ({ language }) => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqItems = [
    {
      questionZh: '如何計算我們在一起的天數？',
      questionEn: 'How do I calculate days we\'ve been together?',
      answerZh: '在首頁點擊「新增事件」，選擇你們的紀念日期、輸入標題、挑選一個可愛的圖示與主題色，點擊儲存後，系統就會自動幫你算出精確的天數囉！',
      answerEn: 'Click "Add Event" on the home page, select your anniversary date, enter a title, pick a cute icon and theme color. Once saved, we\'ll automatically calculate the exact days for you!'
    },
    {
      questionZh: '如何分享我們專屬的紀念日圖片？',
      questionEn: 'How do I share our beautiful anniversary image?',
      answerZh: '點擊任何一個你建立的紀念日卡片進入預覽，接著點擊下方的「分享」按鈕，你就可以選擇 9 種不同的精美模板，並一鍵下載成完美的 IG 尺寸圖片分享給大家！',
      answerEn: 'Click on any of your events to preview it, then tap the "Share" button. You can choose from 9 beautiful templates and download a perfectly sized image for your IG stories!'
    },
    {
      questionZh: '更換設備後紀錄會不見嗎？',
      questionEn: 'Will I lose my records if I change devices?',
      answerZh: '如果你是「訪客模式」，資料只會存在目前的瀏覽器中。強烈建議你點擊右上角「登入 / 註冊」建立帳號，這樣所有的甜蜜回憶就會安全地存入雲端，不管用手機還是電腦都能隨時查看！',
      answerEn: 'If you are in "Guest Mode", data is only saved in your current browser. We highly recommend creating an account so all your sweet memories are safely backed up to the cloud and accessible across all devices!'
    },
    {
      questionZh: '愛情成就和里程碑是什麼？',
      questionEn: 'What are love achievements and milestones?',
      answerZh: '當你們在一起的天數達到特定數字（例如 100天、520天、1314天），系統就會自動為你們解鎖專屬的「里程碑徽章」，就像遊戲破關一樣，為你們的愛情增添滿滿儀式感。',
      answerEn: 'When your days together reach specific numbers (like 100, 520, or 1314 days), the system automatically unlocks exclusive "Milestone Badges" for you, adding a sense of ceremony to your relationship.'
    },
    {
      questionZh: '工作日計算器怎麼用？',
      questionEn: 'How to use the working days calculator?',
      answerZh: '在首頁上方切換到「日期計算」，選擇開始與結束日期後，將計算模式切換為「工作日」，系統就會自動幫你扣除六日，精準算出還剩幾個工作天！',
      answerEn: 'Switch to the "Date Calculator" tab, select start and end dates, and set the mode to "Workdays". It will automatically exclude weekends and calculate the exact business days!'
    }
  ];

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-8">
      {/* 標題區 */}
      <div className="text-center mb-12">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="inline-block"
        >
          <div className="bg-white dark:bg-slate-800 hand-drawn-box py-3 px-8 inline-flex items-center gap-3 mb-4">
            <SafeIcon icon={FiIcons.FiHelpCircle} className="text-3xl text-pink-500" />
            <h1 className="text-3xl font-black text-gray-800 dark:text-white mb-0">
              {language === 'zh' ? '需要幫忙嗎？' : 'Need Some Help?'}
            </h1>
          </div>
        </motion.div>
        <p className="text-gray-600 dark:text-gray-400 font-medium mt-4">
          {language === 'zh' 
            ? '別擔心，我們整理了大家最常問的可愛問題，希望能解答你的疑惑！' 
            : 'Don\'t worry, we\'ve compiled the most frequently asked questions here!'}
        </p>
      </div>

      {/* 手風琴 FAQ 區塊 */}
      <div className="mb-16">
        {faqItems.map((item, index) => (
          <FAQItem
            key={index}
            question={language === 'zh' ? item.questionZh : item.questionEn}
            answer={language === 'zh' ? item.answerZh : item.answerEn}
            isOpen={openIndex === index}
            onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
          />
        ))}
      </div>

      {/* 關於我們 */}
      <motion.section
        id="about"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="mb-10 hand-drawn-box bg-white dark:bg-slate-800 p-8 md:p-10"
      >
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-pink-100 dark:bg-pink-900/40 text-pink-500 flex items-center justify-center shrink-0">
            <SafeIcon icon={FiIcons.FiHeart} className="text-2xl" />
          </div>
          <div>
            <h2 className="text-2xl font-black text-gray-800 dark:text-white mb-4">
              {language === 'zh' ? '誰在打造 DaysMatter？' : 'Who Is Behind DaysMatter?'}
            </h2>
            <p className="text-gray-600 dark:text-gray-300 font-medium leading-relaxed">
              {language === 'zh'
                ? 'DaysMatter 由 Homy Tech Limited 開發與營運。我們希望用簡單、溫暖又可愛的設計，幫助每個人記住值得期待的未來，也好好珍藏已經走過的重要日子。'
                : 'DaysMatter is developed and operated by Homy Tech Limited. We create simple, warm, and delightful digital tools that help people look forward to meaningful moments and treasure the important days already shared.'}
            </p>
            <a
              href="https://homy.hk"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-5 text-sm font-bold text-pink-500 hover:text-pink-600 dark:text-pink-400 dark:hover:text-pink-300 transition-colors"
            >
              <SafeIcon icon={FiIcons.FiExternalLink} />
              <span>Homy Tech Limited</span>
            </a>
          </div>
        </div>
      </motion.section>

      {/* 聯絡我們 (明信片風格) */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="relative bg-[#fdfbf7] dark:bg-slate-800 p-8 md:p-12 shadow-md overflow-hidden"
        style={{
          border: '1px solid #e5e7eb',
          borderRadius: '2px',
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 27px, #e5e7eb 27px, #e5e7eb 28px)'
        }}
      >
        {/* 郵票裝飾 */}
        <div className="absolute top-6 right-6 w-16 h-20 bg-pink-100 dark:bg-pink-900/50 border-2 border-dashed border-white shadow-sm flex items-center justify-center transform rotate-6">
          <SafeIcon icon={FiIcons.FiHeart} className="text-pink-400 text-2xl" />
        </div>
        
        <div className="relative z-10 max-w-md">
          <h2 className="text-2xl font-black text-gray-800 dark:text-white mb-4 bg-white/80 dark:bg-slate-800/80 inline-block px-2">
            {language === 'zh' ? '還是找不到答案嗎？' : 'Still Have Questions?'}
          </h2>
          <p className="text-gray-700 dark:text-gray-300 font-medium leading-relaxed mb-8 bg-white/80 dark:bg-slate-800/80 inline-block p-2">
            {language === 'zh' 
              ? '如果你遇到任何 Bug，或是對我們有任何浪漫的新功能提案，都非常歡迎寫信告訴我們喔！' 
              : 'If you encounter any bugs or have romantic new feature ideas, we\'d love to hear from you!'}
          </p>
          
          <a 
            href="mailto:info@homy.hk" 
            className="inline-flex items-center gap-3 px-6 py-4 bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-bold rounded-xl shadow-lg hover:-translate-y-1 hover:shadow-xl transition-all active:scale-95"
          >
            <SafeIcon icon={FiIcons.FiSend} className="text-xl" />
            <span>{language === 'zh' ? '寫信給我們 (info@homy.hk)' : 'Email Us (info@homy.hk)'}</span>
          </a>
        </div>
      </motion.div>
    </div>
  );
};

export default Help;